<script setup lang="ts">
import { onUnmounted, ref } from 'vue';

import { AppButton, AppModal, AppFadeTransition } from '@libs/ui';

import DrawerButton from './DrawerButton.vue';

interface PeekPriceModalMobileLayoutEmits {
  (eventName: 'cancel'): void;
  (eventName: 'confirm'): void;
  (eventName: 'layout-shift'): void;
}

const emit = defineEmits<PeekPriceModalMobileLayoutEmits>();

const extended = ref<boolean>(false);

onUnmounted(() => (extended.value = false));

function onLayoutShift(): void {
  extended.value = !extended.value;
  emit('layout-shift');
}
</script>

<template>
  <AppModal @close="emit('cancel')">
    <div class="peek-price-modal-mobile-layout">
      <div class="calendar-wrapper" :class="{ extended: extended }">
        <AppFadeTransition transition-time="long" transition-type="fade-in">
          <template v-if="extended">
            <div class="calendar-inner">
              <slot name="priceCalendar" />
            </div>
          </template>
        </AppFadeTransition>
        <AppFadeTransition transition-time="long" transition-type="fade-in">
          <template v-if="!extended">
            <slot name="priceRibbon" />
          </template>
        </AppFadeTransition>
      </div>

      <div class="drawer" :class="{ extended: extended }">
        <DrawerButton
          :is-extended="extended"
          class="drawer-button"
          @click="onLayoutShift"
        />

        <div class="drawer-content">
          <slot name="itinerary" />
        </div>
      </div>

      <div class="footer">
        <AppButton
          type="secondary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('cancel')"
        >
          {{ $t('planner.close') }}
        </AppButton>
        <AppButton
          type="primary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('confirm')"
        >
          {{ $t('planner.select') }}
        </AppButton>
      </div>
    </div>
  </AppModal>
</template>

<style scoped lang="scss">
.peek-price-modal-mobile-layout {
  --price-ribbon-height: 160px;
  --price-calendar-height: 440px;
  --footer-height: 77px;

  height: 100%;
  overflow: scroll;

  * {
    box-sizing: border-box;
  }

  .calendar-wrapper {
    padding: var(--spacing-16);
    height: var(--price-ribbon-height);
    transition: height var(--transition-medium);

    &.extended {
      height: var(--price-calendar-height);
    }

    // NOTE: This inner div exists to make price calendar
    // render with fade-in, instead of fade-in + grow.
    .calendar-inner {
      height: var(--price-calendar-height);
      overflow: hidden;
    }
  }

  .drawer {
    display: flex;
    flex-direction: column;

    position: relative;
    background-color: var(--color-white);
    min-height: calc(100% - var(--price-ribbon-height));

    // NOTE:  This before element is used to apply box-shadow over drawer.
    // It could be done in one line by moving box-shadow property into .drawer,
    // but this causes visual artifact during height transition.
    &::before {
      box-shadow: var(--shadow-elevation-40);
      border-top-left-radius: var(--border-radius-20);
      border-top-right-radius: var(--border-radius-20);
      width: 100%;
      height: 20px;
      top: 0;
      left: 0;
      position: absolute;
      z-index: -1;
      content: '';
    }

    &.extended {
      min-height: calc(100% - var(--price-calendar-height));

      .drawer-content {
        padding-bottom: calc(var(--footer-height) + var(--spacing-16));
      }
    }

    border-top-left-radius: var(--border-radius-20);
    border-top-right-radius: var(--border-radius-20);
    border: 1px solid var(--color-neutral-30);
    padding: var(--spacing-32) var(--spacing-16) 0 var(--spacing-16);

    .drawer-button {
      position: absolute;
      top: -14px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 4;
    }

    .drawer-content {
      height: 100%;
      padding-bottom: calc(var(--footer-height) + var(--spacing-64));
    }
  }

  .footer {
    display: flex;
    gap: var(--spacing-12);
    padding: var(--spacing-16);
    border-top: 1px solid var(--color-neutral-30);
    background-color: var(--color-white);
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
  }
}
</style>
